import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from 'shared/lib/utils';

interface FormTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
}

const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  ({ label, error, hint, required, className, id, rows = 3, ...rest }, ref) => {
    const { t } = useTranslation();
    const inputId = id || `field-${label.replace(/\s+/g, '-').toLowerCase()}`;
    const errorMessage = error ? t(`validation.${error}`, { defaultValue: error }) : undefined;

    return (
      <div className="flex flex-col gap-2 w-full" data-error={error ? 'true' : undefined}>
        <label htmlFor={inputId} className="text-base font-medium text-gray-700">
          {label}
          {required && <span className="text-gray-500 ms-1">*</span>}
        </label>
        <textarea
          id={inputId}
          ref={ref}
          rows={rows}
          aria-invalid={!!error}
          className={cn('input-modern resize-none', error && 'has-error', className)}
          {...rest}
        />
        {errorMessage ? (
          <span className="text-xs font-medium text-red-600">{errorMessage}</span>
        ) : hint ? (
          <span className="text-xs text-gray-500">{hint}</span>
        ) : null}
      </div>
    );
  },
);

FormTextarea.displayName = 'FormTextarea';

export default FormTextarea;
